import { Formik } from 'formik';
import _ from 'lodash';
import moment from 'moment';
import pluralize from 'pluralize';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import {
  ActionButton,
  Buttons,
  CancelButton,
  DeleteButton,
  Drawer,
  Field,
  Form,
  FormMessage,
  Icon,
  ProjectTaskTagMultiSelect,
  Tab,
  Tabs,
  Tag,
  Tags,
  TaskCategoryIcon,
} from '~/components';
import HoursProgress from '~/components/HoursProgress';
import RevenueProgress from '~/components/RevenueProgress';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useDocumentTitle, useForm } from '~/hooks';
import projectTaskStatuses from '~/lookups/project-task-statuses';
import { colors, weights } from '~/styles';
import { dateFormats, emptyStringToNull, mergeValues } from '~/utils';
import CapFeesCheckbox from './CapFeesCheckbox';
import ProjectTaskHistory from './ProjectTaskHistory';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-top: 1.625rem;
  margin-bottom: 1.625rem;
`;

const RollupProgress = styled.div`
  legend > span:last-child {
    color: ${colors.grey55};
  }
`;

const RollupSubtitle = styled.div`
  margin-top: 1.5rem;
  color: ${colors.grey40};
  font-size: 0.75rem;
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;

  &:first-child {
    margin-top: 0;
  }
`;

const RollupHighlights = styled.div`
  display: flex;
  margin-top: 1rem;
`;

const RollupHighlight = styled.div`
  display: flex;
  align-items: center;
  color: ${colors.grey55};
  font-size: 0.75rem;

  &:not(:first-child) {
    padding-left: 0.75rem;
  }

  &:not(:last-child) {
    padding-right: 0.75rem;
    border-right: 1px solid ${colors.grey10};
  }
`;

const RollupStatus = styled.span`
  width: 0.625rem;
  height: 0.625rem;
  margin-right: 0.5rem;
  background-color: ${({ $statusId }) =>
    ({ not_started: colors.warning, in_progress: colors.success, completed: colors.black })[$statusId] ||
    colors.grey40};
  border-radius: 50%;
`;

const RollupSubtaskTags = styled(Tags)`
  margin: 0.5rem -0.25rem 0;
  font-size: 0.75rem;
`;

const RollupSubtaskTag = styled(Tag)`
  background-color: ${colors.primary10};
`;

function ProjectTaskRollupForm({ task, project, onSaved, onClose, onDelete }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [{ isSubmitting, status, message, saved }, form] = useForm();
  const [hasDependencies, setHasDependencies] = useState(true);
  const [descendants, setDescendants] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);
  const toast = useToast();

  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).projects(project.id).tasks(task.id).hasDependencies();
      setHasDependencies(data);
    })();
  }, [api, workspace.id, project.id, task]);

  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).projects(project.id).tasks(task.id).descendants();
      setDescendants(data);
    })();
  }, [api, workspace.id, project.id, task]);

  const title = 'Edit Task';
  useDocumentTitle(title);

  const formRef = useRef();
  const firstFieldRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);

  const defaultValues = {
    code: '',
    description: '',
    name: '',
    taskCategory: null,
    statusId: 'not_started',
    capHours: false,
    capFees: false,
    tags: [],
    parent: null,
  };

  const initialValues = mergeValues(defaultValues, task);

  const tabErrors = {
    task: ['code', 'description', 'name'],
  };

  return (
    <Drawer
      title={title}
      isOpen
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        async function handleSubmit(values) {
          try {
            form.submit(formRef.current.status.action);

            const body = emptyStringToNull({
              ..._.omit(values, ['tags', 'parent', 'taskCategory']),
              projectTaskTagAssignments: values.tags.map((tag) => ({ projectTaskTagId: tag.id })),
              parentId: values.parent?.id ?? null,
              projectTaskCategoryId: values.taskCategory?.id ?? null,
            });

            const { data } = await api.www.workspaces(workspace.id).projects(project.id).tasks(task.id).upsert(body);

            await onSaved(data);

            toast.success('Task saved successfully');
            form.save(formRef.current.status.action);
            closeDrawer();
          } catch ({ message }) {
            form.error({ message });
          }
        }

        async function handleDelete() {
          await onDelete(task);
          closeDrawer();
        }

        return (
          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            initialStatus={{ next: 'done' }}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              code: Yup.string().label('Task Code').max(255),
              description: Yup.string().label('Notes').max(5000),
              name: Yup.string().label('Task Name').max(255).required(),
            })}>
            {({ errors, values, setFieldValue, submitForm, setStatus }) => {
              const submit = async (action) => {
                setStatus({ action });
                submitForm();
              };

              return (
                <Form>
                  <Tabs selectedIndex={tabIndex} onChange={(index) => setTabIndex(index)}>
                    <Tab>
                      Task
                      {tabErrors.task.some((key) => !!errors[key]) && (
                        <Icon icon="exclamation-circle" color={colors.danger} spaceLeft />
                      )}
                    </Tab>
                    {task.permissions.manage && <Tab>History</Tab>}
                  </Tabs>
                  <Content>
                    {status && <FormMessage.Error>{message}</FormMessage.Error>}

                    {
                      [
                        <React.Fragment key="task">
                          <Form.Section title="Basic Information">
                            <Form.Control>
                              <Field.Text ref={firstFieldRef} name="name" placeholder="Task Name" maxLength={255} />
                            </Form.Control>
                            <Form.Control>
                              <Field.ProjectTaskCategorySelect
                                name="taskCategory"
                                placeholder="Rollup Task Category"
                                type="rollup"
                                clearable={false}
                              />
                            </Form.Control>
                            <Form.Control>
                              <Field.ProjectTaskSelect name="parent" placeholder="Parent Task" project={project} />
                            </Form.Control>
                            <Form.Control>
                              <Field.Text name="code" placeholder="Task Code" maxLength={255} />
                            </Form.Control>
                            <Form.Control>
                              <Field.Control>
                                <ProjectTaskTagMultiSelect
                                  name="tags"
                                  placeholder="Tags"
                                  value={values.tags}
                                  allowNew
                                  onChange={({ target: { value } }) => setFieldValue('tags', value)}
                                />
                              </Field.Control>
                            </Form.Control>
                            <Form.Control>
                              <Field.TextArea name="description" placeholder="Notes" rows={4} maxLength={5000} />
                            </Form.Control>
                          </Form.Section>

                          <Form.Section title="Summary">
                            <div>
                              <RollupProgress>
                                <RollupSubtitle>Hours</RollupSubtitle>
                                <HoursProgress worked={task.hours} estimated={task.budgetHours} isRollup />

                                {task.permissions.viewRates && task.permissions.viewRevenue && project.isBillable && (
                                  <>
                                    <RollupSubtitle>Revenue</RollupSubtitle>
                                    <RevenueProgress
                                      earned={task.rollupRevenue}
                                      budgeted={task.budgetRevenue}
                                      currency={project.currency}
                                      isRollup
                                    />
                                  </>
                                )}
                              </RollupProgress>

                              <RollupSubtitle>Details</RollupSubtitle>
                              <RollupHighlights>
                                {values.taskCategory && (
                                  <RollupHighlight>
                                    <TaskCategoryIcon
                                      icon={values.taskCategory.icon}
                                      color={values.taskCategory.color}
                                    />
                                    {values.taskCategory.name}
                                  </RollupHighlight>
                                )}
                                {descendants.length > 0 && (
                                  <RollupHighlight>
                                    {descendants.length} {pluralize('subtask', descendants.length)}
                                  </RollupHighlight>
                                )}
                                <RollupHighlight>
                                  <RollupStatus $statusId={values.statusId} />
                                  {projectTaskStatuses[values.statusId]?.name}
                                </RollupHighlight>
                                {(values.start || values.end) && (
                                  <RollupHighlight>
                                    {values.start && values.end ? (
                                      <span>
                                        {moment(values.start).format(dateFormats.compactDate)} –{' '}
                                        {moment(values.end).format(dateFormats.compactDate)}
                                      </span>
                                    ) : values.start ? (
                                      <span>Starts: {moment(values.start).format(dateFormats.compactDate)}</span>
                                    ) : (
                                      <span>Ends: {moment(values.end).format(dateFormats.compactDate)}</span>
                                    )}
                                  </RollupHighlight>
                                )}
                              </RollupHighlights>

                              {descendants.length > 0 && (
                                <>
                                  <RollupSubtitle>Subtasks</RollupSubtitle>
                                  <RollupSubtaskTags>
                                    {descendants.map((descendant) => (
                                      <RollupSubtaskTag key={descendant.id}>
                                        <TaskCategoryIcon
                                          icon={descendant.taskCategory?.icon}
                                          color={descendant.taskCategory?.color}
                                        />
                                        {descendant.name}
                                      </RollupSubtaskTag>
                                    ))}
                                  </RollupSubtaskTags>
                                </>
                              )}
                            </div>
                          </Form.Section>

                          <Form.Section title="Settings">
                            <Form.Control>
                              <Field.Checkbox
                                name="capHours"
                                label="Cap the hours to the total budgeted for this task"
                              />
                            </Form.Control>
                            {project.isBillable && (
                              <Form.Control>
                                <CapFeesCheckbox project={project} />
                              </Form.Control>
                            )}
                          </Form.Section>
                        </React.Fragment>,
                        <ProjectTaskHistory key="history" projectId={project.id} taskId={task.id} />,
                      ][tabIndex]
                    }
                  </Content>

                  <Drawer.Actions>
                    {task.permissions.manage && _.isFunction(onDelete) && (
                      <DeleteButton disabled={hasDependencies} onClick={handleDelete}>
                        Delete
                      </DeleteButton>
                    )}

                    <Buttons align="right">
                      <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                      <ActionButton
                        isLoading={isSubmitting === 'close'}
                        ok={saved === 'close'}
                        onClick={() => submit('close')}>
                        Save &amp; Close
                      </ActionButton>
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}

export default ProjectTaskRollupForm;
